//DUCKS pattern
import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    DashboardData,
    DashboardDataReply,
    DashboardDataRequest,
    ExtendedMonitoredTv,
    MonitoredMcdmMeasure,
    NetworkDashboardData,
    NetworkMapConvection,
} from 'app/model/SOL49';
import { ServiceFactory } from 'app/service/ServiceFactory';
import { ErrorResponse, McdmState } from 'app/model/CORE';
import Utils from 'app/common/util/Utils';
import { RootState } from 'app/redux/store';
import AppContext from 'app/common/context/AppContext';
import memoizeOne from 'memoize-one';
import MeasureUtils from 'app/common/util/MeasureUtils';
import TimePeriodUtils from 'app/common/util/TimePeriodUtils';
import MonitoredLoadUtils from 'app/common/util/MonitoredLoadUtils';
import {
    DEFAULT_WHOLE_NETWORK_SETTINGS,
    WholeNetworkSettings,
} from 'app/components/dashboard/network/flows/settings/WholeNetworkSettings';
import { SUPERVISED_AREAS } from 'app/components/dashboard/common/DashboardConstants';

//type of the state managed by the reducer
interface DashboardDataState {
    getConfigDataStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
    dashboardData?: DashboardData;
    convectionData?: NetworkMapConvection;
    getDataVersionStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
    getDashboardDataStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
    getDashboardDataError: string | null | ErrorResponse;
    //TODO: move to map slice
    loadConvection: number; // if any panel requires the convection increases the number by one and when it doesn't need it, decreases by one. then, the fetch will load convection if this values is higher than 1
    currentDataVersion: number;
    newChatMessages: number;
    drawerOpen: boolean;
    globalTextFilter: string;
    settings: WholeNetworkSettings;
    filteredTvSets: string[];
    filteredTvs: ExtendedMonitoredTv[];
    nbHiddenTvs: number;
    nbMonitorableTvs: number;
    nbLoadedTvs: number;
    nbSupervisedAndRegulated: number;
}

const initialState: DashboardDataState = {
    //empty
    getConfigDataStatus: 'idle',
    getDataVersionStatus: 'idle',
    getDashboardDataStatus: 'idle',
    getDashboardDataError: null,
    loadConvection: 0,
    currentDataVersion: undefined,
    newChatMessages: 0,
    drawerOpen: false,
    globalTextFilter: '',
    settings: DEFAULT_WHOLE_NETWORK_SETTINGS,
    filteredTvSets: [],
    filteredTvs: [],
    nbHiddenTvs: 0,
    nbLoadedTvs: 0,
    nbMonitorableTvs: 0,
    nbSupervisedAndRegulated: 0,
};

const dashboardDataSlice = createSlice({
    name: 'dashboardData', // string that will be used as the prefix for generated action types
    initialState,
    reducers: {
        setDataVersion(state: DashboardDataState, action: PayloadAction<number>) {
            state.currentDataVersion = action.payload;
        },
        setLoadConvection(state: DashboardDataState, action: PayloadAction<number>) {
            state.loadConvection += action.payload;
        },
        setDashboardData(state: DashboardDataState, action: PayloadAction<DashboardDataReply>) {
            state.dashboardData = action.payload.dashboardData;
            state.convectionData = action.payload.mapConvection;
        },
        setNewChatMessages(state: DashboardDataState, action: PayloadAction<number>) {
            state.newChatMessages = action.payload;
        },
        setDrawerOpen(state: DashboardDataState, action: PayloadAction<boolean>) {
            state.drawerOpen = action.payload;
        },
        setPartialDashboardData(state: DashboardDataState, action: PayloadAction<Partial<DashboardData>>) {
            state.dashboardData = { ...state.dashboardData, ...action.payload };
        },
        setTextFilter: (state, action: PayloadAction<string>) => {
            state.globalTextFilter = action.payload;
            updateFilteredInfo(state);
        },
        setFlowSettings: (state, action: PayloadAction<WholeNetworkSettings>) => {
            state.settings = action.payload;
            updateFilteredInfo(state);
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getDashboardData.pending, (state, action) => {
                state.getDashboardDataStatus = 'loading';
            })
            .addCase(getDashboardData.fulfilled, (state, action) => {
                state.getDashboardDataStatus = 'succeeded';
                if (action.payload.error) {
                    state.getDashboardDataStatus = 'failed';
                    state.getDashboardDataError = action.payload.error;
                } else {
                    state.dashboardData = {
                        ...action.payload.data.dashboardData,
                        monitoredMcdmMeasures: filteredMcdmMeasures(
                            action.payload.data.dashboardData.monitoredMcdmMeasures,
                            AppContext.getZones().flat(2),
                        ),
                    };
                    state.convectionData = action.payload.data.mapConvection;
                }
            })
            .addCase(getDashboardData.rejected, (state, action) => {
                state.getDashboardDataStatus = 'failed';
                state.getDashboardDataError = action.error.message;
            })
            .addCase(getDashboardFilteredData.pending, (state, action) => {
                state.getDashboardDataStatus = 'loading';
            })
            .addCase(getDashboardFilteredData.fulfilled, (state, action) => {
                state.getDashboardDataStatus = 'succeeded';
                if (action.payload.error) {
                    state.getDashboardDataStatus = 'failed';
                    state.getDashboardDataError = action.payload.error;
                } else {
                }
            })
            .addCase(getDashboardFilteredData.rejected, (state, action) => {
                state.getDashboardDataStatus = 'failed';
                state.getDashboardDataError = action.error.message;
            })
            .addCase(getDataVersion.pending, state => {
                state.getDataVersionStatus = 'loading';
            })
            .addCase(getDataVersion.fulfilled, (state, action) => {
                state.getDataVersionStatus = 'succeeded';
                if (state.currentDataVersion !== action.payload.data) {
                    state.currentDataVersion = action.payload.data;
                }
            })
            .addCase(getDataVersion.rejected, state => {
                state.getDataVersionStatus = 'failed';
            })
            .addCase(getConfigData.pending, state => {
                state.getConfigDataStatus = 'loading';
            })
            .addCase(getConfigData.fulfilled, (state, action) => {
                state.getConfigDataStatus = 'succeeded';
            })
            .addCase(getConfigData.rejected, state => {
                state.getConfigDataStatus = 'failed';
            })
            .addCase(getDashboardDataForCurrentVersion.pending, state => {
                state.getDashboardDataStatus = 'loading';
            })
            .addCase(getDashboardDataForCurrentVersion.fulfilled, (state, action) => {
                state.getDashboardDataStatus = 'succeeded';
                if (action.payload.error) {
                    state.getDashboardDataStatus = 'failed';
                    state.getDashboardDataError = action.payload.error;
                    state.currentDataVersion = undefined; //so it loads it again
                } else {
                    state.dashboardData = {
                        ...action.payload.data.dashboardData,
                        monitoredMcdmMeasures: filteredMcdmMeasures(
                            action.payload.data.dashboardData.monitoredMcdmMeasures,
                            AppContext.getZones().flat(2),
                        ),
                    };
                    state.convectionData = action.payload.data.mapConvection;
                    updateFilteredInfo(state);
                }
            })
            .addCase(getDashboardDataForCurrentVersion.rejected, (state, action) => {
                state.getDashboardDataStatus = 'failed';
                state.getDashboardDataError = action.error.message;
            });
    },
});

const filteredMcdmMeasures = memoizeOne(
    (monitoredMcdmMeasures: Array<MonitoredMcdmMeasure>, monitoredZones: string[]) => {
        return monitoredMcdmMeasures?.filter(
            m =>
                !monitoredZones ||
                monitoredZones.includes(MeasureUtils.getTvLocation(m.measure).refLocation?.refId?.substring(0, 2)),
        );
    },
);

export const getDashboardData = createAsyncThunk('posts/getDashboardData', async (request: DashboardDataRequest) => {
    return await ServiceFactory.sol49Service().getDashboardData(request);
});

export const getDashboardDataForCurrentVersion = createAsyncThunk(
    'posts/getDashboardDataForCurrentVersion',
    async (request: DashboardDataRequest, thunkAPI) => {
        const { currentDataVersion } = (thunkAPI.getState() as RootState).dashboardData;
        const { mapSettings } = (thunkAPI.getState() as RootState).mapSettingsData;
        request.version = currentDataVersion;
        request.mapConvectionRequest = mapSettings?.showConvection
            ? { minLevel: mapSettings.convectionRiskLevel }
            : undefined;
        return await ServiceFactory.sol49Service().getDashboardData(request);
    },
    {
        condition: (_, { getState }) =>
            new Promise(resolve => {
                const { currentDataVersion } = (getState() as RootState).dashboardData;
                resolve(Utils.isExists(currentDataVersion)); //if there is no current version don't do the call
            }),
    },
);

export const getDashboardFilteredData = createAsyncThunk(
    'posts/getDashboardFilteredData',
    async (request: DashboardDataRequest) => {
        return await ServiceFactory.sol49Service().getDashboardFilteredData(request);
    },
);

/*export const getDataVersion = createAsyncThunk('posts/getDataVersion', async () => {
    return await ServiceFactory.sol49Service().getDataVersion();
})*/

export const getDataVersion = createAsyncThunk(
    'posts/getDataVersion',
    async () => {
        return await ServiceFactory.sol49Service().getDataVersion(AppContext.getDataset().type);
    },
    {
        condition: (any, { getState }) =>
            new Promise(resolve => {
                const { getDashboardDataStatus, getDataVersionStatus, getConfigDataStatus } = (getState() as RootState)
                    .dashboardData;
                //if currently getting dashboard data then don't call getDataVersion
                resolve(
                    'loading' !== getDashboardDataStatus &&
                        getDataVersionStatus !== 'loading' &&
                        getConfigDataStatus !== 'loading',
                );
            }),
    },
);

export const getConfigData = createAsyncThunk('posts/getConfigData', async () => {
    return await ServiceFactory.technicalService().getConfigData();
});

export const {
    setLoadConvection,
    setDataVersion,
    setDashboardData,
    setPartialDashboardData,
    setNewChatMessages,
    setDrawerOpen,
    setTextFilter,
    setFlowSettings,
} = dashboardDataSlice.actions;

export const selectMonitoredTvByTvIds = createSelector(
    [
        // Usual first input - extract value from `state`
        (state: DashboardDataState) => state.dashboardData.monitoredTvList,
        // Take the second arg, `tvIds`, and forward to the output selector
        (state: DashboardDataState, tvIds: Array<string>) => tvIds,
    ],
    // Output selector gets (`items, tvIds)` as args
    (items: Array<ExtendedMonitoredTv>, tvIds: Array<string>) =>
        items?.filter(item => Utils.contains(tvIds, item.refLocation.id)),
);

export const selectTvSetMap = (state: RootState) =>
    (state.dashboardData.dashboardData as NetworkDashboardData).tvSetMap;

export const selectMonitoredMeasures = (state: RootState) => state.dashboardData.dashboardData.monitoredMeasures;
export const selectConvectionData = (state: RootState) => state.dashboardData.convectionData;
export const selectDashboardData = (state: RootState) => state.dashboardData.dashboardData;
export const selectFilteredTvs = (state: RootState) => state.dashboardData.filteredTvs;
export const selectFilteredTvSets = (state: RootState) => state.dashboardData.filteredTvSets;
export default dashboardDataSlice.reducer;

function updateFilteredInfo(state: DashboardDataState) {
    let nbMonitorableTvs = 0;
    let nbLoadedTvs = 0;
    let nbSupervisedAndRegulated = 0;
    const tvSets: Set<string> = new Set<string>();
    const data = state.dashboardData as NetworkDashboardData;

    let monitoredTvs = data.monitoredTvList;
    if (state.globalTextFilter) {
        const textFilterUC: Array<string> = Utils.toUpperCase(state.globalTextFilter).split(' ');
        monitoredTvs = data.monitoredTvList?.filter(monitoredTv => {
            const tvId = Utils.toUpperCase(monitoredTv.refLocation.id);
            const tvSet = Utils.toUpperCase(data.tvSetMap[tvId]);
            //if the query text has a * uses the simpleWildcardEquals, otherwise checks if a tv or tvSet contains the given query text
            const matchingTextFilters = textFilterUC.filter(textFilterEl => {
                const re = Utils.wildcardToRegex(textFilterEl);
                return re.test(tvId) || re.test(tvSet);
            });
            //return tvId?.indexOf(textFilterUC) >= 0 || tvSet?.indexOf(textFilterUC) >= 0
            return matchingTextFilters.length > 0;
        });
    }

    const filteredTvs = monitoredTvs?.filter(monitoredTv => {
        let result = true;
        const tvSet = data.tvSetMap[monitoredTv.refLocation.id];

        const isRegulated = data.monitoredMeasures.some(
            m => MeasureUtils.getTvLocation(m.measure).id === monitoredTv.refLocation.id,
        );
        const isSupervised = isSupervisedTvSet(tvSet);
        if (state.settings.tv === 'supervised') {
            if (!isSupervised) result = false;
            else nbSupervisedAndRegulated += 1;
        } else if (state.settings.tv === 'regulated') {
            if (!isRegulated) result = false;
            else nbSupervisedAndRegulated += 1;
        } else if (state.settings.tv === 'supervised + regulated') {
            if (!isRegulated || !isSupervised) result = false;
            else nbSupervisedAndRegulated += 1;
        }

        const activeLoadValues = monitoredTv.loadValues.filter(loadValue =>
            monitoredTv.activePeriods.some(tp => TimePeriodUtils.intersects(tp, loadValue.timePeriod)),
        );
        const isLoaded = MonitoredLoadUtils.isEntryDisplayed(activeLoadValues, state.settings.load);
        if (!isLoaded) result = false;
        else nbLoadedTvs += 1;

        const isMonitorable = monitoredTv.monitorable || state.settings.showNonMonitorableTv;
        if (!isMonitorable) result = false;
        else nbMonitorableTvs += 1;

        if (result) {
            tvSets.add(tvSet);
        }
        return result;
    });

    const measureProposalTvs = data.monitoredMcdmMeasures?.filter(
        proposal => proposal.mcdmMeasureTopic.state === McdmState.PROPOSED,
    );
    // Add TFVs concerned by measure proposals not yet ACK
    if (measureProposalTvs) {
        const tvsWithProposals = measureProposalTvs.map(proposal => MeasureUtils.getTvLocation(proposal.measure).id);
        const monitoredTvsWithProposal = monitoredTvs.filter(tv => Utils.contains(tvsWithProposals, tv.refLocation.id));
        monitoredTvsWithProposal.forEach(mtv => {
            tvSets.add(data.tvSetMap[mtv.refLocation.id]);
        });
        Utils.addAllIfNeeded(filteredTvs, monitoredTvsWithProposal);
    }
    const nbHiddenTvs = monitoredTvs.length - filteredTvs.length;
    const filteredTvSets = Array.from(tvSets);

    state.filteredTvs = filteredTvs;
    state.filteredTvSets = filteredTvSets;
    state.nbHiddenTvs = nbHiddenTvs;
    state.nbLoadedTvs = nbLoadedTvs;
    state.nbMonitorableTvs = nbMonitorableTvs;
    state.nbSupervisedAndRegulated = nbSupervisedAndRegulated;
}

const isSupervisedTvSet = (tvSet: string) => {
    return Utils.isNotEmpty(SUPERVISED_AREAS) && Utils.contains(SUPERVISED_AREAS, tvSet?.substring(0, 2));
};
